import './RepairAttemptIndicator.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.10 (2026-06-06) — RepairAttemptIndicator.
 *
 * Pure-presentation render of a `force_repair_attempt` dispatch action.
 * Ch 11 equipment damage + repair: D6 damage roll maps to Light (1-3,
 * 1× base time), Medium (4-5, 2×), Heavy (6, 3×).
 *
 * Schema 1-to-1 with the V9.1.c tool args at routes/dispatch.py:887-930:
 *   - d6_damage_roll: integer 1-6 (required)
 *   - equipment_type: free-text string (required)
 *   - has_repair_skill: boolean (some equipment can't be repaired
 *     without a skill per Ch 11 + Demolitions skill REPAIR field)
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Surfaces SETUP. Resolved repair-time multiplier (1× / 2× / 3×) is
 * derived deterministically from the d6 roll — no engine call needed.
 */

export interface RepairAttemptIndicatorProps {
  d6DamageRoll: number;
  equipmentType: string;
  hasRepairSkill?: boolean;
  rationale?: string;
  /** Optional resolved repair time in minutes/hours (engine output). */
  resolvedRepairTimeHours?: number | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

type DamageLevel = 'Light' | 'Medium' | 'Heavy';

function damageLevelFromD6(roll: number): DamageLevel | null {
  if (roll >= 1 && roll <= 3) return 'Light';
  if (roll === 4 || roll === 5) return 'Medium';
  if (roll === 6) return 'Heavy';
  return null;
}

const DAMAGE_MULTIPLIER: Record<DamageLevel, number> = {
  Light: 1,
  Medium: 2,
  Heavy: 3,
};

export function RepairAttemptIndicator({
  d6DamageRoll,
  equipmentType,
  hasRepairSkill = true,
  rationale,
  resolvedRepairTimeHours,
  onDismiss,
  testidPrefix = 'br-repair',
}: RepairAttemptIndicatorProps) {
  const damageLevel = damageLevelFromD6(d6DamageRoll);
  const isValidRoll = damageLevel !== null;
  const multiplier = isValidRoll ? DAMAGE_MULTIPLIER[damageLevel] : 0;
  const skillBlocked = !hasRepairSkill;
  const hasResolvedTime =
    typeof resolvedRepairTimeHours === 'number' && resolvedRepairTimeHours >= 0;

  return (
    <aside
      className="br-repair"
      data-testid={testidPrefix}
      data-damage-level={damageLevel ?? 'invalid'}
      data-has-skill={hasRepairSkill ? 'true' : 'false'}
      data-d6={d6DamageRoll}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-repair-header">
        <span className="br-repair-label">Repair</span>
        <span
          className="br-repair-equipment"
          data-testid={`${testidPrefix}-equipment`}
        >
          {equipmentType}
        </span>
      </header>

      {isValidRoll ? (
        <div
          className="br-repair-damage-row"
          data-testid={`${testidPrefix}-damage-row`}
        >
          <span
            className="br-repair-damage-chip"
            data-testid={`${testidPrefix}-damage-chip`}
            data-damage-level={damageLevel}
          >
            {damageLevel}
          </span>
          <span
            className="br-repair-d6 br-label"
            data-testid={`${testidPrefix}-d6-roll`}
          >
            D6 = {d6DamageRoll}
          </span>
          <span
            className="br-repair-multiplier br-label"
            data-testid={`${testidPrefix}-multiplier`}
          >
            {multiplier}× base time
          </span>
        </div>
      ) : (
        <p
          className="br-repair-invalid"
          data-testid={`${testidPrefix}-invalid-roll`}
        >
          Invalid D6 roll: {d6DamageRoll}. Must be 1-6.
        </p>
      )}

      {skillBlocked && (
        <p
          className="br-repair-skill-block"
          data-testid={`${testidPrefix}-skill-block`}
        >
          No relevant repair skill — attempt may be impossible per Ch 11.
        </p>
      )}

      {hasResolvedTime && (
        <div
          className="br-repair-resolved"
          data-testid={`${testidPrefix}-resolved`}
        >
          <span className="br-repair-resolved-label">Repair time</span>
          <span
            className="br-repair-resolved-value br-label"
            data-testid={`${testidPrefix}-resolved-time`}
          >
            {resolvedRepairTimeHours} hr
          </span>
        </div>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-repair-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
